// Coach Profile Types and Interfaces

export type CoachType = 
  | 'FITNESS' 
  | 'NUTRITION' 
  | 'WELLNESS' 
  | 'LIFE' 
  | 'BUSINESS' 
  | 'EXECUTIVE' 
  | 'HEALTH' 
  | 'SPORTS' 
  | 'RECOVERY' 
  | 'HOLISTIC';

export type BusinessType = 
  | 'INDIVIDUAL' 
  | 'BUSINESS' 
  | 'FRANCHISE' 
  | 'EMPLOYEE';

export type TimezoneFlexibility = 
  | 'STRICT' 
  | 'MODERATE' 
  | 'FLEXIBLE';

export type BackgroundCheckStatus = 
  | 'PENDING' 
  | 'PASSED' 
  | 'REVIEW';

export interface Certification {
  name: string;
  issuer: string;
  date_obtained: string;
  expiry_date?: string;
}

export interface Education {
  degree: string;
  institution: string;
  year: string;
}

export interface PricingTier {
  name: string;
  price: number;
  duration: number;
  description: string;
}

export interface CancellationPolicy {
  notice_hours: number;
  refund_percentage: number;
  late_cancellation_fee?: number;
}

export interface TargetDemographics {
  age_range?: string;
  fitness_level?: string;
  goals?: string[];
}

export interface WorkingHours {
  [day: string]: {
    enabled: boolean;
    start?: string;
    end?: string;
  };
}

export interface VacationDate {
  start_date: string;
  end_date: string;
  reason?: string;
}

export interface CoachUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
}

export interface CoachAvailability {
  id?: number;
  primary_timezone: string;
  display_timezones?: string[];
  auto_adjust_dst?: boolean;
  dst_notification_days?: number;
  working_hours: WorkingHours;
  buffer_between_sessions?: number;
  advance_booking_days?: number;
  minimum_notice_hours?: number;
  session_durations?: number[];
  default_session_duration?: number;
  recurring_breaks?: any[];
  vacation_dates?: VacationDate[];
  overtime_available?: boolean;
  overtime_rate_multiplier?: number;
  weekend_available?: boolean;
  holiday_available?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface UniversalCoachProfile {
  id?: number;
  user?: CoachUser;
  coach_type: CoachType;
  coach_type_display?: string;
  secondary_types?: CoachType[];
  
  // Professional Info
  bio: string;
  specializations: string[];
  certifications: Certification[];
  experience_years: number;
  education?: Education[];
  
  // Business Profile
  business_name?: string;
  business_type?: BusinessType;
  business_type_display?: string;
  business_registration?: string;
  
  // Business Settings
  timezone: string;
  availability_rules?: any;
  pricing_tiers: PricingTier[];
  cancellation_policy?: CancellationPolicy;
  payment_methods?: string[];
  
  // Marketing Profile
  public_profile_url?: string;
  marketing_tagline?: string;
  unique_selling_points?: string[];
  target_demographics?: TargetDemographics;
  profile_image?: string;
  cover_image?: string;
  intro_video_url?: string;
  
  // Performance Metrics
  overall_rating?: number;
  total_reviews?: number;
  total_clients_served?: number;
  active_clients?: number;
  client_retention_rate?: number;
  average_client_duration_months?: number;
  
  // Financial Metrics
  monthly_revenue?: number;
  lifetime_revenue?: number;
  average_client_value?: number;
  
  // AI Collaboration
  ai_usage_score?: number;
  ai_accuracy_feedback?: any;
  predictive_insights_accuracy?: number;
  
  // Compliance
  hipaa_compliant?: boolean;
  hipaa_cert_date?: string;
  insurance_verified?: boolean;
  insurance_expiry?: string;
  background_check_date?: string;
  background_check_status?: BackgroundCheckStatus;
  
  // Client Management
  accepting_clients?: boolean;
  max_clients?: number;
  waitlist_enabled?: boolean;
  client_screening_required?: boolean;
  client_screening_questions?: string[];
  
  // International Support
  accepts_international_clients?: boolean;
  languages_spoken?: string[];
  timezone_flexibility?: TimezoneFlexibility;
  
  // Profile Status
  profile_completion_percentage?: number;
  onboarding_completed?: boolean;
  onboarding_step?: string;
  is_verified?: boolean;
  is_compliant?: boolean;
  can_accept_clients?: boolean;
  
  // Metadata
  created_at?: string;
  updated_at?: string;
  verified_at?: string;
  featured?: boolean;
  active?: boolean;
  
  // Related
  availability?: CoachAvailability;
}

// Coach List Item (for search results)
export interface CoachListItem {
  id: number;
  user_name: string;
  coach_type: CoachType;
  coach_type_display: string;
  specializations: string[];
  overall_rating: number;
  total_reviews: number;
  profile_image?: string;
  marketing_tagline?: string;
  accepting_clients: boolean;
  public_profile_url: string;
}

// Coach Public Profile (for public viewing)
export interface CoachPublicProfile {
  id: number;
  user_name: string;
  coach_type: CoachType;
  coach_type_display: string;
  bio: string;
  specializations: string[];
  experience_years: number;
  education?: Education[];
  business_name?: string;
  marketing_tagline?: string;
  unique_selling_points?: string[];
  profile_image?: string;
  cover_image?: string;
  intro_video_url?: string;
  overall_rating: number;
  total_reviews: number;
  accepting_clients: boolean;
  languages_spoken?: string[];
  timezone: string;
  public_profile_url: string;
}

// Onboarding Types
export interface OnboardingProgress {
  profile_completion_percentage: number;
  onboarding_completed: boolean;
  current_step: string;
  completed_steps: string[];
  remaining_steps: string[];
  next_step?: {
    key: string;
    name: string;
    required: boolean;
  };
}

export interface OnboardingStep {
  key: string;
  name: string;
  completed: boolean;
  required: boolean;
}

export interface OnboardingSteps {
  [key: string]: OnboardingStep;
}

// API Response Types
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Form Data Types for Updates
export interface BasicInfoFormData {
  bio: string;
  specializations: string[];
  secondary_types?: CoachType[];
  languages_spoken?: string[];
}

export interface ProfessionalInfoFormData {
  certifications: Certification[];
  experience_years: number;
  education?: Education[];
}

export interface BusinessSetupFormData {
  business_type: BusinessType;
  business_name?: string;
  timezone: string;
  accepts_international_clients?: boolean;
  timezone_flexibility?: TimezoneFlexibility;
  max_clients?: number;
  client_screening_required?: boolean;
  client_screening_questions?: string[];
}

export interface AvailabilityFormData {
  primary_timezone: string;
  working_hours: WorkingHours;
  session_durations?: number[];
  default_session_duration?: number;
  buffer_between_sessions?: number;
  advance_booking_days?: number;
  minimum_notice_hours?: number;
  weekend_available?: boolean;
  holiday_available?: boolean;
}

export interface PricingFormData {
  pricing_tiers: PricingTier[];
  payment_methods?: string[];
  cancellation_policy?: CancellationPolicy;
}

export interface MarketingFormData {
  marketing_tagline?: string;
  unique_selling_points?: string[];
  target_demographics?: TargetDemographics;
  intro_video_url?: string;
}

// Coach Type Display Names
export const COACH_TYPE_DISPLAY: Record<CoachType, string> = {
  FITNESS: 'Fitness Coach',
  NUTRITION: 'Nutrition Coach',
  WELLNESS: 'Mental Wellness Coach',
  LIFE: 'Life Coach',
  BUSINESS: 'Business Coach',
  EXECUTIVE: 'Executive Coach',
  HEALTH: 'Health Coach',
  SPORTS: 'Sports Performance Coach',
  RECOVERY: 'Recovery & Rehabilitation Coach',
  HOLISTIC: 'Holistic Wellness Coach',
};

// Business Type Display Names
export const BUSINESS_TYPE_DISPLAY: Record<BusinessType, string> = {
  INDIVIDUAL: 'Individual Coach',
  BUSINESS: 'Coaching Business',
  FRANCHISE: 'Franchise/Multi-location',
  EMPLOYEE: 'Employee/Contractor',
};

// Timezone Flexibility Display Names
export const TIMEZONE_FLEXIBILITY_DISPLAY: Record<TimezoneFlexibility, string> = {
  STRICT: 'Strict - Only my timezone',
  MODERATE: 'Moderate - Some flexibility',
  FLEXIBLE: 'Flexible - Any timezone',
};